// Thread: the active conversation — message history + composer. Own messages sit
// right (brand), others left. Auto-scrolls to newest; Enter sends, Shift+Enter
// newlines. Optimistic sends show immediately (seq at the bottom until the server
// seq lands); a failed send is surfaced inline.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Video, Play, Compass, Info } from 'lucide-react';
import { ReceiptTicks, receiptStateFor } from './ReceiptTicks';
import { MessageInfoDialog } from './MessageInfoDialog';
import { tickFor, type ConversationReceipts } from '../readState';
import { openProviderUrl } from '@/lib/providerLinks';
import { getApiErrorMessage } from '@/lib/apiError';
import { useLocation, useNavigate } from 'react-router-dom';
import { launchWeb } from '@/features/web/lib/launch';
import { buildTenantPath, extractTenantSlug } from '@/features/navigation/utils';
import { confirmVoiceAction, cancelVoiceAction } from '@/lib/danaVoiceApi';
import { DANA_BOT_USER_ID, REACTION_GLYPH, REACTION_KEYS } from '../types';
import { isNearTop } from '../messageHistory';
import type { Availability, DanaProposal, Message, MessageCard, ReactionKey } from '../types';
import type { Directory } from '../hooks/useDirectory';
import { Composer, type ReplyTarget } from './Composer';
import { PollMessage } from './PollMessage';
import { PersonAvatar } from './PersonAvatar';
import { AvailabilityBadge, AvailabilityDotOverlay } from './AvailabilityBadge';
import { conversationsApi } from '../api/conversationsApi';
import { splitMessage, type MentionTarget } from '../mentions';
import { ImageLightbox } from './ImageLightbox';
import { PdfPreviewDialog } from './PdfPreviewDialog';
import {
  attachmentErrorMessage, officeUnavailableMessage, previewKind, saveBlob, type OfficeUnavailableReason,
} from '../attachments';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';

function prettySize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

const FILE_CATEGORIES = ['Legal', 'Payroll', 'Benefits', 'HR', 'Tax'] as const;

// Loads an image attachment for the inline thumbnail through the gated API (never a
// bare <img src> at the endpoint: that can't renew an expired session or explain a
// failure). The object URL lives exactly as long as the card.
function useAttachmentImage(conversationId: string, messageId: string, enabled: boolean) {
  const [state, setState] = useState<{ url?: string; error?: string; loading: boolean }>({ loading: enabled });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let url: string | undefined;
    let cancelled = false;
    setState({ loading: true });
    conversationsApi
      .fetchFile(conversationId, messageId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setState({ url, loading: false });
      })
      .catch((e) => {
        if (!cancelled) setState({ error: attachmentErrorMessage(e), loading: false });
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [conversationId, messageId, enabled, nonce]);
  return { ...state, retry: () => setNonce((n) => n + 1) };
}

// An uploaded file attachment rendered as a card. Every open — thumbnail, preview,
// download — fetches the bytes fresh through the membership-gated endpoint from the
// message id (see ../attachments.ts); nothing about the file is cached as a URL.
// PDFs and images preview in place; Office files (Word, PowerPoint, Excel, ODF) get
// View, which shows the PDF the server prepares from them (Office preview, spec
// §3.4); everything else downloads under its original name. Download always saves
// the ORIGINAL file. While the upload is in flight (optimistic, no key yet) it
// shows "Uploading…". HR/admin get a "File to…" menu to file it into a shared
// company Document Category.
export const FileMessage: React.FC<{
  msg: Message;
  canFileCompany?: boolean;
  onFileToCategory?: (category: string) => Promise<void>;
}> = ({ msg, canFileCompany, onFileToCategory }) => {
  const { t } = useTranslation('conversations');
  const f = msg.file!;
  const pending = msg.seq === Number.MAX_SAFE_INTEGER || !f.key;
  const [menu, setMenu] = useState(false);
  const [filed, setFiled] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const sub = [prettySize(f.size), f.saved_to_documents ? t('file.saved') : '']
    .filter(Boolean)
    .join(' · ');

  const fileTo = async (cat: string) => {
    if (busy || !onFileToCategory) return;
    setBusy(true);
    setErr('');
    try {
      await onFileToCategory(cat);
      setFiled(cat);
      setMenu(false);
    } catch (e) {
      setErr(getApiErrorMessage(e, t('file.file_failed')));
    } finally {
      setBusy(false);
    }
  };

  const kind = previewKind(f.mime, f.name);
  const thumb = useAttachmentImage(msg.conversation_id, msg.id, kind === 'image' && !pending);
  // The open preview: an object URL over a freshly fetched, re-typed blob.
  const [preview, setPreview] = useState<{ url: string; blob: Blob; kind: 'pdf' | 'image' } | null>(null);
  const [opening, setOpening] = useState<'preview' | 'download' | null>(null);
  const [openErr, setOpenErr] = useState('');
  const lastAction = useRef<'preview' | 'download'>('download');

  // Office View: the server converts on first view, so it may say "preparing" for
  // a while; the viewer opens at once and shows each state. `viewToken` makes a
  // closed viewer ignore (and stop) the request it started.
  type OfficeView =
    | { state: 'preparing' }
    | { state: 'ready'; url: string }
    | { state: 'unavailable'; reason: OfficeUnavailableReason }
    | { state: 'error'; message: string };
  const [office, setOffice] = useState<OfficeView | null>(null);
  // Preview switched off (404): stop offering View on this card.
  const [noView, setNoView] = useState(false);
  const viewToken = useRef(0);
  // Aborts the request in flight when the viewer closes or the card goes away.
  const viewAbort = useRef<AbortController | null>(null);
  // Leaving the conversation (or the card disappearing) cancels the view: a PDF
  // that answers afterwards is dropped and never becomes a URL (round 2).
  useEffect(() => () => {
    viewToken.current += 1;
    viewAbort.current?.abort();
  }, []);

  const viewOffice = async () => {
    const token = ++viewToken.current;
    const current = () => token === viewToken.current;
    viewAbort.current?.abort();
    const ctl = new AbortController();
    viewAbort.current = ctl;
    setOffice({ state: 'preparing' });
    try {
      const r = await conversationsApi.fetchOfficePreview(msg.conversation_id, msg.id, {
        onPreparing: () => { if (current()) setOffice({ state: 'preparing' }); },
        cancelled: () => !current(),
        signal: ctl.signal,
      });
      if (!current() || r.kind === 'cancelled') return;
      if (r.kind === 'ready') {
        // Typed by us, never by the server's or uploader's MIME (as for PDFs above).
        setOffice({ state: 'ready', url: URL.createObjectURL(new Blob([r.blob], { type: 'application/pdf' })) });
      } else {
        setOffice({ state: 'unavailable', reason: r.reason });
        if (r.reason === 'off') setNoView(true);
      }
    } catch (e) {
      if (current()) setOffice({ state: 'error', message: attachmentErrorMessage(e) });
    }
  };

  const closeOffice = useCallback(() => {
    viewToken.current += 1;
    viewAbort.current?.abort();
    setOffice((o) => {
      if (o?.state === 'ready') URL.revokeObjectURL(o.url);
      return null;
    });
  }, []);
  useEffect(() => () => { if (office?.state === 'ready') URL.revokeObjectURL(office.url); }, [office]);

  const closePreview = useCallback(() => {
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p.url);
      return null;
    });
  }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const open = async (action: 'preview' | 'download') => {
    if (opening) return;
    lastAction.current = action;
    setOpening(action);
    setOpenErr('');
    try {
      const raw = await conversationsApi.fetchFile(msg.conversation_id, msg.id);
      // Office files never render their own bytes here: View goes through the
      // server-made PDF (viewOffice); this path only downloads them.
      if (action === 'download' || kind === 'none' || kind === 'office') {
        saveBlob(raw, f.name);
        return;
      }
      // Re-type from what we decided to render, never from the uploader-supplied
      // MIME: a same-origin blob frame must never render HTML that claims to be a PDF.
      const blob = new Blob([raw], { type: kind === 'pdf' ? 'application/pdf' : raw.type || f.mime || 'image/png' });
      setPreview({ url: URL.createObjectURL(blob), blob, kind });
    } catch (e) {
      setOpenErr(attachmentErrorMessage(e));
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="w-full max-w-[75%] rounded-2xl border border-border bg-card p-3 shadow-sm">
      {kind === 'image' && !pending && (
        <div className="mb-2">
          {thumb.url ? (
            <button
              type="button"
              onClick={() => void open('preview')}
              aria-label={t('file.view_image', { defaultValue: 'View {{name}}', name: f.name }) as string}
              className="block w-full cursor-zoom-in rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <img src={thumb.url} alt={f.name} className="max-h-72 w-auto max-w-full rounded-xl object-contain" />
            </button>
          ) : thumb.loading ? (
            <div className="h-24 w-full animate-pulse rounded-xl bg-muted" aria-label={t('file.loading', { defaultValue: 'Loading…' }) as string} />
          ) : (
            <div role="alert" className="rounded-xl border border-border bg-muted p-3 text-xs text-muted-foreground">
              <p>{thumb.error}</p>
              <button type="button" onClick={thumb.retry} className="mt-1 font-medium text-primary-600 hover:underline">
                {t('file.retry', { defaultValue: 'Try again' })}
              </button>
            </div>
          )}
        </div>
      )}
      {preview?.kind === 'image' && (
        <ImageLightbox
          src={preview.url}
          alt={f.name}
          caption={f.name}
          onDownload={() => saveBlob(preview.blob, f.name)}
          downloadLabel={t('file.download') as string}
          closeLabel={t('common.close', { defaultValue: 'Close' }) as string}
          onClose={closePreview}
        />
      )}
      {office && (
        <PdfPreviewDialog
          src={office.state === 'ready' ? office.url : undefined}
          name={f.name}
          onDownload={() => void open('download')}
          downloadLabel={t('file.download', { defaultValue: 'Download' }) as string}
          closeLabel={t('common.close', { defaultValue: 'Close' }) as string}
          onClose={closeOffice}
          notice={openErr ? (
            // A failed Download shows HERE, over the open PDF, not only on the card behind the viewer.
            <div role="alert" className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-foreground">
              <p>{openErr}</p>
              <button type="button" onClick={() => void open(lastAction.current)} className="mt-0.5 font-medium text-primary-600 hover:underline">
                {t('file.retry', { defaultValue: 'Try again' })}
              </button>
            </div>
          ) : undefined}
        >
          {office.state === 'preparing' && (
            <p role="status" className="animate-pulse text-muted-foreground">
              {t('preview.preparing', { defaultValue: 'Preparing preview…' })}
            </p>
          )}
          {office.state === 'unavailable' && (
            <>
              <p className="font-semibold">{t('preview.unavailable_title', { defaultValue: 'Preview isn’t available for this file' })}</p>
              <p className="text-muted-foreground">{officeUnavailableMessage(office.reason)}</p>
              <button
                type="button"
                onClick={() => void open('download')}
                disabled={!!opening}
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
              >
                {opening === 'download' ? t('file.opening', { defaultValue: 'Opening…' }) : t('file.download', { defaultValue: 'Download' })}
              </button>
            </>
          )}
          {office.state === 'error' && (
            <div role="alert">
              <p>{office.message}</p>
              <button type="button" onClick={() => void viewOffice()} className="mt-1 font-medium text-primary-600 hover:underline">
                {t('file.retry', { defaultValue: 'Try again' })}
              </button>
            </div>
          )}
        </PdfPreviewDialog>
      )}
      {preview?.kind === 'pdf' && (
        <PdfPreviewDialog
          src={preview.url}
          name={f.name}
          onDownload={() => saveBlob(preview.blob, f.name)}
          downloadLabel={t('file.download') as string}
          closeLabel={t('common.close', { defaultValue: 'Close' }) as string}
          onClose={closePreview}
        />
      )}
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary-50 text-lg dark:bg-primary-500/15">{kind === 'image' ? '🖼️' : '📄'}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-charcoal">{f.name}</p>
          {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
        </div>
        {pending ? (
          <span className="flex-none text-xs italic text-muted-foreground">{t('sending')}</span>
        ) : (
          <div className="flex flex-none items-center gap-1.5">
            {kind === 'office' && !noView && (
              <button
                type="button"
                onClick={() => void viewOffice()}
                disabled={!!opening || !!office}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-charcoal hover:bg-muted disabled:opacity-60"
              >
                {t('preview.view', { defaultValue: 'View' })}
              </button>
            )}
            {kind === 'pdf' && (
              <button
                type="button"
                onClick={() => void open('preview')}
                disabled={!!opening}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-charcoal hover:bg-muted disabled:opacity-60"
              >
                {opening === 'preview' ? t('file.opening', { defaultValue: 'Opening…' }) : t('file.preview', { defaultValue: 'Preview' })}
              </button>
            )}
            <button
              type="button"
              onClick={() => void open('download')}
              disabled={!!opening}
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
            >
              {opening === 'download' ? t('file.opening', { defaultValue: 'Opening…' }) : t('file.download', { defaultValue: 'Download' })}
            </button>
          </div>
        )}
      </div>
      {openErr && (
        <div role="alert" className="mt-2 rounded-lg border border-border bg-muted px-2.5 py-1.5 text-xs text-foreground">
          <p>{openErr}</p>
          <button type="button" onClick={() => void open(lastAction.current)} className="mt-0.5 font-medium text-primary-600 hover:underline">
            {t('file.retry', { defaultValue: 'Try again' })}
          </button>
        </div>
      )}
      {!pending && canFileCompany && onFileToCategory && (
        <div className="relative mt-2 border-t border-border pt-2">
          {filed ? (
            <p className="text-xs text-green-600">✓ {t('file.filed', { category: filed })}</p>
          ) : (
            <button type="button" onClick={() => setMenu((v) => !v)} disabled={busy} className="text-xs font-medium text-primary-600 hover:underline disabled:opacity-50">
              {busy ? t('file.filing') : t('file.file_to')}
            </button>
          )}
          {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
          {menu && !filed && (
            <div className="absolute bottom-7 left-0 z-10 flex flex-col rounded-lg border border-border bg-popover p-1 shadow-md">
              {FILE_CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => fileTo(c)} className="rounded px-3 py-1 text-left text-xs text-charcoal hover:bg-muted">
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Dana confirm-first proposal (§12.3): what Dana wants to do, awaiting the user's
// confirm. Confirm/Dismiss call the existing ai-agent actions endpoint — nothing runs
// until the user says so, and the action is actor-scoped server-side.
// Remember a proposal's resolution across reloads (the persisted proposal on the
// message would otherwise re-show Confirm; the confirm endpoint is idempotent, but a
// stale button is confusing). Best-effort localStorage.
type ProposalState = 'idle' | 'working' | 'done' | 'dismissed' | 'error';
const proposalKey = (id: string) => `kp-dana-proposal:${id}`;
const readProposalState = (id: string): ProposalState => {
  try {
    const v = localStorage.getItem(proposalKey(id));
    return v === 'done' || v === 'dismissed' ? v : 'idle';
  } catch {
    return 'idle';
  }
};
const writeProposalState = (id: string, v: 'done' | 'dismissed') => {
  try {
    localStorage.setItem(proposalKey(id), v);
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
};

const ProposalCard: React.FC<{
  proposal: DanaProposal;
  /** Whose proposal this is. Absent on messages from before this was stamped. */
  ownerUserId?: string;
  selfId?: string;
  nameOf?: (userId?: string) => string;
}> = ({ proposal, ownerUserId, selfId, nameOf }) => {
  const { t } = useTranslation('conversations');
  const [state, setState] = useState<ProposalState>(() => readProposalState(proposal.action_id));
  const [err, setErr] = useState('');
  const fields = Object.entries(proposal.payload ?? {}).filter(
    ([, v]) => v != null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'),
  );
  // Somebody else's proposal. Absent owner (older messages) means we cannot
  // tell, and the safe answer there is the old behaviour, not hiding the
  // button from the person who can actually use it.
  const theirs = !!ownerUserId && !!selfId && ownerUserId !== selfId;
  const confirm = async () => {
    setState('working');
    try {
      await confirmVoiceAction(proposal.action_id, proposal.payload_sha256);
      setState('done');
      writeProposalState(proposal.action_id, 'done');
    } catch (e) {
      // ⚠️ The server's own words, never the transport's. This used to show
      // `e.message`, which produced "Request failed with status code 404" —
      // reported from production 2026-10-06. A status code is not an
      // explanation, and this repo's rule is getApiErrorMessage.
      setErr(getApiErrorMessage(e, 'Could not complete this action.'));
      setState('error');
    }
  };
  const dismiss = async () => {
    setState('dismissed');
    writeProposalState(proposal.action_id, 'dismissed');
    cancelVoiceAction(proposal.action_id).catch(() => undefined);
  };
  return (
    <div className="mt-1 w-full max-w-[85%] rounded-xl border border-primary-200 bg-primary-50/60 p-3 dark:bg-primary-500/10">
      <p className="text-sm font-semibold text-charcoal">{proposal.summary || 'Dana wants to do something'}</p>
      {fields.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
          {fields.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="capitalize text-muted-foreground">{k.replace(/_/g, ' ')}</dt>
              <dd className="truncate text-charcoal">{String(v)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {/* ⚠️ Only the person who asked can confirm. ai-agent's action is
          actor-bound and answers 404 to anybody else, but EVERY participant in
          a shared thread sees this card — so without this the others got a
          live button and a bare "Request failed with status code 404".
          Reported from production 2026-10-06.

          `theirs` is false when ownerUserId is absent, so messages from before
          the owner was stamped keep working exactly as they did. */}
      {state === 'idle' && (theirs ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="proposal-waiting">
          {t('proposal.waiting_for', {
            defaultValue: 'Waiting for {{name}} to confirm',
            name: nameOf?.(ownerUserId) || 'them',
          })}
        </p>
      ) : (
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={confirm} className="rounded-lg bg-primary-500 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-600">
            {t('proposal.confirm')}
          </button>
          <button type="button" onClick={dismiss} className="rounded-lg px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">
            {t('proposal.dismiss')}
          </button>
        </div>
      ))}
      {state === 'working' && <p className="mt-2 text-xs text-muted-foreground">{t('proposal.working')}</p>}
      {state === 'done' && <p className="mt-2 text-xs font-semibold text-green-600">✅ {t('proposal.done')}</p>}
      {state === 'dismissed' && <p className="mt-2 text-xs text-muted-foreground">{t('proposal.dismissed')}</p>}
      {state === 'error' && <p className="mt-2 text-xs text-red-600">{t('proposal.failed')}{err ? `: ${err}` : ''}</p>}
    </div>
  );
};

// Rich object card (assignment, leave, expense, meeting, …) rendered inline in the
// thread. Free-form fields; an optional action link opens the underlying object.
const CardMessage: React.FC<{
  card: MessageCard;
  mine?: boolean;
  onCancelMeeting?: (meetingId: string) => Promise<void>;
  onRescheduleMeeting?: (meetingId: string, input: { startAt: string; endAt?: string; timezone?: string }) => Promise<void>;
}> = ({ card, mine, onCancelMeeting, onRescheduleMeeting }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('conversations');
  const tCard = (k: string, dv: string) => t(k, { defaultValue: dv });
  const [rescheduling, setRescheduling] = useState(false);
  const [rDate, setRDate] = useState('');
  const [rTime, setRTime] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const isMeeting = card.type === 'MEETING';
  const cancelled = (card.status || '').toLowerCase() === 'cancelled';
  const canManageMeeting = isMeeting && !!mine && !cancelled && (!!onCancelMeeting || !!onRescheduleMeeting);
  const zone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();

  const doCancel = async () => {
    if (!onCancelMeeting || !card.entity_id || busy) return;
    if (!(await confirm({ title: tCard('meeting.cancel', 'Cancel meeting'), message: tCard('meeting.cancel_confirm', 'Cancel this meeting for everyone?'), danger: true, confirmLabel: tCard('meeting.cancel', 'Cancel meeting'), cancelLabel: t('common.keep', { defaultValue: 'Keep' }) }))) return;
    setBusy(true);
    try { await onCancelMeeting(card.entity_id); } finally { setBusy(false); }
  };
  const doReschedule = async () => {
    if (!onRescheduleMeeting || !card.entity_id || !rDate || !rTime || busy) return;
    const start = new Date(`${rDate}T${rTime}`);
    if (Number.isNaN(start.getTime())) return;
    setBusy(true);
    try {
      await onRescheduleMeeting(card.entity_id, { startAt: start.toISOString(), endAt: undefined, timezone: zone });
      setRescheduling(false);
    } finally { setBusy(false); }
  };
  const open = () => {
    if (!card.action_url) return;
    const slug = extractTenantSlug(location.pathname);
    navigate(buildTenantPath(card.action_url.replace(/^\/app\/?/, ''), slug));
  };
  const fields = (card.fields ?? []).filter((f) => f?.label && f?.value);
  return (
    <div className="w-full max-w-[85%] rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {card.type && (
            <span className="inline-block rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">
              {card.type.replace(/_/g, ' ')}
            </span>
          )}
          <p className="mt-1 truncate text-sm font-semibold text-charcoal">{card.title || 'Update'}</p>
          {card.subtitle && <p className="text-xs text-muted-foreground">{card.subtitle}</p>}
        </div>
        {card.status && (
          <span className="flex-none rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
            {card.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      {fields.length > 0 && (
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          {fields.map((f) => (
            <React.Fragment key={f.label}>
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="truncate text-charcoal">{f.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      <div className="mt-2 flex items-center gap-3">
        {card.type === 'MEETING' && card.join_url && (
          <a
            href={card.join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
          >
            <Video className="h-3.5 w-3.5" /> {tCard('meeting.join', 'Join meeting')}
          </a>
        )}
        {card.type === 'MUSIC' && card.action_url && (
          <button
            type="button"
            onClick={() => openProviderUrl(card.action_url!)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
          >
            <Play className="h-3.5 w-3.5" /> Play
          </button>
        )}
        {card.type !== 'MUSIC' && card.action_url && !cancelled && (
          <button
            type="button"
            onClick={open}
            className="text-xs font-semibold text-primary-600 hover:text-primary-700"
          >
            {isMeeting ? tCard('meeting.add_to_calendar', 'Add to calendar') : (card.action_label ? `${card.action_label} →` : 'Open →')}
          </button>
        )}
      </div>
      {canManageMeeting && (
        <div className="mt-2 border-t border-border pt-2">
          {rescheduling ? (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1 text-xs text-charcoal" />
              <input type="time" value={rTime} onChange={(e) => setRTime(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1 text-xs text-charcoal" />
              <button type="button" onClick={doReschedule} disabled={busy || !rDate || !rTime} className="rounded-lg bg-primary-500 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">
                {tCard('meeting.save', 'Save')}
              </button>
              <button type="button" onClick={() => setRescheduling(false)} className="text-xs text-muted-foreground">
                {tCard('edit.cancel', 'Cancel')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {onRescheduleMeeting && (
                <button type="button" onClick={() => setRescheduling(true)} className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                  {tCard('meeting.reschedule', 'Reschedule')}
                </button>
              )}
              {onCancelMeeting && (
                <button type="button" onClick={doCancel} disabled={busy} className="text-xs font-semibold text-danger hover:opacity-80 disabled:opacity-50">
                  {tCard('meeting.cancel', 'Cancel meeting')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function timeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Stable per-sender name colour for group/circle threads (WhatsApp-style), so you can
// tell members apart at a glance. Deterministic from the user id → the same person is
// always the same colour. Mid-tone hues read on both the light and dark bubble grounds.
const SENDER_COLORS = [
  'text-rose-500',
  'text-amber-600',
  'text-emerald-600',
  'text-sky-600',
  'text-violet-500',
  'text-fuchsia-500',
  'text-teal-600',
  'text-indigo-500',
];
function senderColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return SENDER_COLORS[h % SENDER_COLORS.length];
}

const ReactionTallies: React.FC<{ msg: Message; onReact: (key: string) => void }> = ({ msg, onReact }) => {
  const entries = Object.entries(msg.reactions ?? {}).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  const mine = new Set(msg.my_reactions ?? []);
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {entries.map(([key, count]) => (
        <button
          key={key}
          type="button"
          onClick={() => onReact(key)}
          className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors ${
            mine.has(key)
              ? 'border-primary-300 bg-primary-50 text-primary-700'
              : 'border-border bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          <span>{REACTION_GLYPH[key as ReactionKey] ?? '•'}</span>
          <span>{count}</span>
        </button>
      ))}
    </div>
  );
};

// Split plain text into text/URL segments so message bodies can render links. Trailing
// sentence punctuation is trimmed off a URL so "see https://x.co." doesn't swallow the dot.
// A message body that linkifies URLs. Each link opens externally on click, and carries a
// small "Open in KaziPeople Web" affordance that launches the browser connected to THIS
// conversation (so the link stays tied to the work being discussed).
const LinkableText: React.FC<{
  text: string; mine: boolean; onOpenInWeb: (url: string) => void; t: (k: string, o?: any) => string;
  mentions?: MentionTarget[];
}> = ({ text, mine, onOpenInWeb, t, mentions }) => {
  const parts = useMemo(() => splitMessage(text, mentions ?? []), [text, mentions]);
  if (parts.length === 1 && parts[0].kind === 'text') {
    return <span className="whitespace-pre-wrap break-words">{text}</span>;
  }
  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((p, i) =>
        p.kind === 'mention' ? (
          <span
            key={i}
            title={p.isSelf ? (t('mention_you', { defaultValue: 'This mentions you' }) as string) : undefined}
            className={`rounded px-1 font-semibold ${
              p.isSelf
                ? 'bg-amber-400/25 text-amber-700 dark:text-amber-300'
                : mine ? 'bg-white/20 text-white' : 'bg-primary-500/15 text-primary-600 dark:text-primary-300'
            }`}
          >
            {p.text}
          </span>
        ) : p.kind === 'url' ? (
          <span key={i} className="inline items-baseline">
            <a
              href={p.text}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline underline-offset-2 ${mine ? 'text-white' : 'text-primary-600 dark:text-primary-300'}`}
            >
              {p.text}
            </a>
            <button
              type="button"
              onClick={() => onOpenInWeb(p.text)}
              aria-label={t('open_in_web', { defaultValue: 'Open in KaziPeople Web' }) as string}
              title={t('open_in_web', { defaultValue: 'Open in KaziPeople Web' }) as string}
              className={`ml-0.5 inline-flex h-4 w-4 translate-y-0.5 items-center justify-center rounded ${mine ? 'text-white/80 hover:bg-white/20' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <Compass className="h-3 w-3" />
            </button>
          </span>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        ),
      )}
    </span>
  );
};

const Bubble: React.FC<{
  msg: Message;
  mine: boolean;
  senderName?: string;
  conversationTitle?: string;
  avatar?: { name: string; url?: string; show: boolean }; // incoming-sender picture column
  onReact: (key: string) => void;
  onEdit?: (body: string) => void;
  onReply?: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
  quoted?: { author: string; snippet: string } | null; // the message this one replies to
  /** People this message really tagged — rendered as chips (see ../mentions). */
  mentions?: MentionTarget[];
  /** Aggregate receipt cursors for the conversation. Undefined = not known yet,
   *  which is also the honest state for messages that predate the feature. */
  receipts?: ConversationReceipts;
  onRetry?: () => void;
  /** Resolves receipt user_ids to names in Message Info. */
  nameOf?: (userId?: string) => string;
}> = ({ msg, mine, senderName, conversationTitle, avatar, onReact, onEdit, onReply, onDelete, canDelete, quoted, mentions, receipts, onRetry, nameOf }) => {
  const { t } = useTranslation('conversations');
  const confirm = useConfirm();
  const navigate = useNavigate();
  const loc = useLocation();
  // Open a link from this message inside KaziPeople Web, connected to this conversation.
  const openInWeb = (url: string) =>
    launchWeb(navigate, loc.pathname, {
      url,
      context: { type: 'conversation', id: msg.conversation_id, title: conversationTitle },
    });
  const dana = msg.sender_user_id === DANA_BOT_USER_ID;
  // A real state, not a prefix sniff. The old test was
  // `kind === 'SYSTEM' && body_plain.startsWith('⚠')`, which hard-coded English
  // into a value and misread any genuine SYSTEM message that opened with a
  // warning sign.
  const failed = msg.send_state === 'failed';
  const optimistic = msg.seq === Number.MAX_SAFE_INTEGER;
  const [showPicker, setShowPicker] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body_plain);
  // Only a sender can edit their own plain text (not cards/gif/poll/system, not Dana).
  const canEdit = !!onEdit && mine && !dana && !failed && !optimistic && msg.kind === 'TEXT';
  // Copy to clipboard, with a moment of visible confirmation — a clipboard
  // write is invisible, and silence after pressing Copy reads as failure.
  const [copied, setCopied] = useState(false);
  const copyBody = async () => {
    const text = msg.body_plain ?? '';
    try {
      // navigator.clipboard needs a secure context; the textarea fallback is
      // what keeps this working on a plain-http dev host.
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error(t('copy.failed', { defaultValue: 'Could not copy the message.' }));
    }
  };

  const beginEdit = () => {
    setDraft(msg.body_plain);
    setEditing(true);
    setShowPicker(false);
  };
  const saveEdit = () => {
    const next = draft.trim();
    if (next && next !== msg.body_plain) onEdit?.(next);
    setEditing(false);
  };

  return (
    <div className={`group flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
      {/* Incoming-sender picture column: avatar on the first message of a run, a spacer
          on the rest so a run's bubbles stay aligned under it. */}
      {avatar && (
        <div className="w-7 flex-none pt-0.5">
          {avatar.show ? <PersonAvatar name={avatar.name} avatarUrl={avatar.url} size={28} /> : null}
        </div>
      )}
      <div className={`flex min-w-0 flex-col ${mine ? 'items-end' : 'items-start'}`}>
        {senderName && !mine && !dana && (
          <span className={`mb-0.5 px-1 text-xs font-semibold ${senderColor(msg.sender_user_id)}`}>{senderName}</span>
        )}
        <div className={`flex items-center gap-1 ${mine ? 'flex-row-reverse' : ''}`}>
        <div
          className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
            failed
              ? 'bg-red-50 text-red-700'
              : dana
                ? 'bg-primary-50 text-primary-900 ring-1 ring-primary-200 dark:bg-primary-500/15 dark:text-primary-50 dark:ring-primary-500/30'
                : mine
                  ? 'bg-primary-500 text-white'
                  : 'bg-muted text-charcoal'
          } ${optimistic ? 'opacity-70' : ''}`}
        >
          {dana && <span className="mb-0.5 block text-xs font-semibold text-primary-700 dark:text-primary-300">Dana</span>}
          {quoted && !editing && (
            <div className={`mb-1.5 rounded-lg border-l-2 px-2 py-1 text-xs ${mine ? 'border-white/60 bg-white/15 text-white/90' : 'border-primary-400 bg-background/60 text-muted-foreground'}`}>
              <div className="font-semibold">{quoted.author}</div>
              <div className="truncate">{quoted.snippet}</div>
            </div>
          )}
          {editing ? (
            // Editing panel sits on its own bg-card surface so text + buttons stay
            // readable in both themes and on either bubble colour (the bubble ground
            // is a fixed brand/muted, which text-charcoal would clash with in dark).
            <div className="flex min-w-[14rem] flex-col gap-1.5 rounded-lg bg-card p-1.5">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveEdit();
                  } else if (e.key === 'Escape') {
                    setEditing(false);
                  }
                }}
                autoFocus
                rows={Math.min(6, draft.split('\n').length)}
                className="w-full resize-none rounded border border-border bg-background px-2 py-1 text-sm text-charcoal focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              <div className="flex items-center justify-end gap-3 text-xs">
                <button type="button" onClick={() => setEditing(false)} className="text-muted-foreground hover:text-charcoal">
                  {t('edit.cancel')}
                </button>
                <button type="button" onClick={saveEdit} className="font-semibold text-primary-600 hover:text-primary-700">
                  {t('edit.save')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <LinkableText text={msg.body_plain} mine={mine} onOpenInWeb={openInWeb} t={t} mentions={mentions} />
              {msg.edited_at && (
                <span className={`ml-1.5 text-[10px] italic ${mine ? 'text-white/60' : 'text-muted-foreground'}`}>({t('edit.edited')})</span>
              )}
              <span className={`ml-2 inline-block align-bottom text-[10px] ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>
                {optimistic ? t('sending') : timeOf(msg.created_at)}
              </span>
              {/* Receipts are the SENDER's information about their own message.
                  Never rendered on an incoming bubble — a recipient has no
                  receipt to show for a message they received. */}
              {mine && (
                <ReceiptTicks
                  state={receiptStateFor(msg.send_state, tickFor(msg.seq, receipts))}
                  onBubble
                  t={t}
                  onRetry={onRetry}
                />
              )}
            </>
          )}
        </div>
        {/* Hover controls — react, and edit for your own text. Hidden while editing. */}
        {!optimistic && !failed && !editing && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            {/* Message info — the SENDER's view of who has seen their own
                message. Never offered on an incoming bubble: the server
                refuses it, and showing the control would promise something
                that cannot work. */}
            {mine && (
              <button
                type="button"
                onClick={() => setShowInfo(true)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                aria-label={t('info.title', { defaultValue: 'Message info' })}
                title={t('info.title', { defaultValue: 'Message info' })}
                data-testid="open-message-info"
              >
                <Info size={13} aria-hidden="true" />
              </button>
            )}
            {/* Copy — the WhatsApp reflex, and the one action people reach for
                on someone else's message. Offered whenever there is text,
                including an attachment's caption, not only on TEXT kinds. */}
            {!!msg.body_plain?.trim() && (
              <button
                type="button"
                onClick={() => void copyBody()}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                aria-label={copied ? t('copy.copied', { defaultValue: 'Copied' }) : t('copy.copy', { defaultValue: 'Copy' })}
                title={copied ? t('copy.copied', { defaultValue: 'Copied' }) : t('copy.copy', { defaultValue: 'Copy' })}
              >
                {copied ? '✓' : '⧉'}
              </button>
            )}
            {onReply && (
              <button
                type="button"
                onClick={onReply}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                aria-label={t('reply.reply', { defaultValue: 'Reply' })}
                title={t('reply.reply', { defaultValue: 'Reply' })}
              >
                ↩
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={beginEdit}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                aria-label={t('edit.edit')}
                title={t('edit.edit')}
              >
                ✏️
              </button>
            )}
            {canDelete && onDelete && (
              <button
                type="button"
                onClick={async () => { if (await confirm({ title: t('delete.delete', { defaultValue: 'Delete' }), message: t('delete.confirm', { defaultValue: 'Delete this message for everyone?' }), danger: true, confirmLabel: t('delete.delete', { defaultValue: 'Delete' }) })) onDelete(); }}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-red-600"
                aria-label={t('delete.delete', { defaultValue: 'Delete' })}
                title={t('delete.delete', { defaultValue: 'Delete' })}
              >
                🗑
              </button>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPicker((v) => !v)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                aria-label="Add reaction"
              >
                🙂
              </button>
              {showPicker && (
                <div className="absolute bottom-7 z-10 flex gap-0.5 rounded-full border border-border bg-card p-1 shadow-md">
                  {REACTION_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => {
                        onReact(k);
                        setShowPicker(false);
                      }}
                      className="rounded-full px-1 text-base hover:bg-muted"
                    >
                      {REACTION_GLYPH[k]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        </div>
        <ReactionTallies msg={msg} onReact={onReact} />
      </div>
      {/* Mounted only once opened, so a thread of 200 bubbles does not carry
          200 idle dialogs — and the fetch happens on open, not on render. */}
      {mine && showInfo && (
        <MessageInfoDialog
          open={showInfo}
          onOpenChange={setShowInfo}
          conversationId={msg.conversation_id}
          messageId={msg.id}
          sentAt={msg.created_at}
          nameOf={nameOf}
          t={t}
        />
      )}
    </div>
  );
};

interface Props {
  title: string;
  messages: Message[];
  loading: boolean;
  currentUserId?: string;
  directory: Directory;
  /** The open conversation — @recognition records it as the source. */
  conversationId?: string;
  /** Aggregate receipt cursors for this conversation, from useConversations.
   *  Undefined = not known yet, which is also the honest state for messages
   *  that predate the feature — they render a single grey check. */
  receipts?: ConversationReceipts;
  showSenders?: boolean; // label each incoming message with its sender (group/circle threads)
  headerAvatar?: { name: string; url?: string }; // the person you're talking to (DM)
  /** Their availability (DM only) — a dot on the avatar + the status inline. */
  headerAvailability?: Availability;
  headerRight?: React.ReactNode;
  hideHeader?: boolean;
  draftKey?: string;
  onSend: (body: string, mentionUserIds: string[], replyToMessageId?: string) => void;
  onSendGif?: (gif: import('../types').MessageGif) => void;
  onSendPoll?: (poll: import('../types').NewPoll) => void;
  onSendFile?: (file: File) => void;
  onCreateAssignment?: (input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string }) => Promise<void>;
  onFileToCategory?: (messageId: string, category: string) => Promise<void>;
  canFileCompany?: boolean;
  canAssign?: boolean;
  selfId?: string;
  onVotePoll?: (messageId: string, optionIds: string[]) => void;
  onReact: (messageId: string, key: string) => void;
  onEdit?: (messageId: string, body: string) => void;
  onDelete?: (messageId: string) => void;
  /** OWNER/ADMIN may delete anyone's message (moderation); senders always may. */
  canModerate?: boolean;
  onReply?: (target: ReplyTarget) => void;
  replyTo?: ReplyTarget | null;
  onCancelReply?: () => void;
  onCancelMeeting?: (meetingId: string) => Promise<void>;
  onRescheduleMeeting?: (meetingId: string, input: { startAt: string; endAt?: string; timezone?: string }) => Promise<void>;
  onTyping?: () => void;
  typingLabel?: string;
  /** Fetch the page before the oldest message shown. */
  onLoadOlder?: () => Promise<void>;
  canLoadOlder?: boolean;
  loadingOlder?: boolean;
}

export const Thread: React.FC<Props> = ({
  title,
  messages,
  loading,
  currentUserId,
  directory,
  conversationId,
  receipts,
  showSenders,
  headerAvatar,
  headerAvailability,
  headerRight,
  hideHeader,
  draftKey,
  onSend,
  onSendGif,
  onSendPoll,
  onSendFile,
  onCreateAssignment,
  onFileToCategory,
  canFileCompany,
  canAssign,
  selfId,
  onVotePoll,
  onReact,
  onEdit,
  onDelete,
  canModerate,
  onReply,
  replyTo,
  onCancelReply,
  onCancelMeeting,
  onRescheduleMeeting,
  onTyping,
  typingLabel,
  onLoadOlder,
  canLoadOlder,
  loadingOlder,
}) => {
  const { t } = useTranslation('conversations');
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ⚠️ Older messages are PREPENDED, which grows the list upwards and pushes
  // everything down — the thread would jump out from under the reader. Record
  // the distance from the BOTTOM before loading and restore it afterwards:
  // that distance is unchanged by anything added above.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onLoadOlder || !canLoadOlder) return;
    if (!isNearTop(el.scrollTop)) return;
    const fromBottom = el.scrollHeight - el.scrollTop;
    void onLoadOlder().then(() => {
      requestAnimationFrame(() => {
        const e2 = scrollRef.current;
        if (e2) e2.scrollTop = e2.scrollHeight - fromBottom;
      });
    });
  }, [onLoadOlder, canLoadOlder]);

  // The bounded context window @recognition may send. Built from the messages
  // already on screen — no extra fetch, and nothing the reader cannot already
  // see. The server trims it again (10 × 600 chars); this only avoids shipping
  // a whole thread across the wire to be thrown away.
  const recognitionContext = useMemo(
    () =>
      messages
        .filter((m) => !m.deleted_at && m.kind === 'TEXT' && m.seq !== Number.MAX_SAFE_INTEGER)
        .slice(-10)
        .map((m) => ({
          message_id: m.id,
          author_user_id: m.sender_user_id,
          text: m.body_plain,
          created_at: m.created_at,
        })),
    [messages],
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <header className="flex flex-none items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {headerAvatar && (
              <AvailabilityDotOverlay availability={headerAvailability}>
                <PersonAvatar name={headerAvatar.name} avatarUrl={headerAvatar.url} size={32} />
              </AvailabilityDotOverlay>
            )}
            <span className="flex min-w-0 flex-col">
              <h2 className="truncate text-sm font-semibold text-charcoal">{title}</h2>
              <AvailabilityBadge availability={headerAvailability} />
            </span>
          </div>
          {headerRight}
        </header>
      )}

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {loadingOlder && (
          <p className="py-2 text-center text-xs text-muted-foreground">
            {t('history.loading', { defaultValue: 'Loading earlier messages…' })}
          </p>
        )}
        {loading && messages.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t('loading')}</div>
        ) : messages.length === 0 ? (
          <div className="mt-8 text-center text-sm text-muted-foreground">{t('no_messages')}</div>
        ) : (
          (() => {
            const visible = messages.filter((m) => !m.deleted_at);
            return visible.map((m, i) => {
              const mine = m.sender_user_id === currentUserId;
              const dana = m.sender_user_id === DANA_BOT_USER_ID;
              // Sender label: only in group/circle threads, only on others' messages, and
              // only on the first message of a run (consecutive messages from one person
              // aren't re-labelled). Dana carries its own label already.
              const prev = visible[i - 1];
              const firstOfRun = !prev || prev.sender_user_id !== m.sender_user_id;
              const senderName =
                showSenders && !mine && !dana && firstOfRun ? directory.nameOf(m.sender_user_id) : undefined;
              const senderTag = senderName ? (
                <span className={`mb-0.5 px-1 text-xs font-semibold ${senderColor(m.sender_user_id)}`}>{senderName}</span>
              ) : null;
              // Incoming-sender picture (DM + group): show it on the first of a run, spacer
              // otherwise so the run aligns. Own + Dana messages carry no directory avatar.
              const avatar =
                !mine && !dana
                  ? { name: directory.nameOf(m.sender_user_id), url: directory.personOf(m.sender_user_id)?.avatarUrl, show: firstOfRun }
                  : undefined;
              const avatarCol = avatar ? (
                <div className="w-7 flex-none pt-0.5">
                  {avatar.show ? <PersonAvatar name={avatar.name} avatarUrl={avatar.url} size={28} /> : null}
                </div>
              ) : null;
              // Who this message actually tagged — the ids the server validated, resolved
              // to the names the composer inserted, so the body can show them as chips.
              const mentionTargets: MentionTarget[] = (m.mentioned_user_ids ?? []).map((id) => ({
                userId: id,
                name: id === DANA_BOT_USER_ID ? 'Dana' : directory.nameOf(id),
                isSelf: !!currentUserId && id === currentUserId,
              }));

              return m.card ? (
                <div key={m.id} className="flex justify-start">
                  <CardMessage card={m.card} mine={mine} onCancelMeeting={onCancelMeeting} onRescheduleMeeting={onRescheduleMeeting} />
                </div>
              ) : m.gif ? (
                <div key={m.id} className={`flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
                  {avatarCol}
                  <div className={`flex min-w-0 flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    {senderTag}
                    <img
                      src={m.gif.url}
                      alt={m.gif.title || 'GIF'}
                      className={`max-w-[70%] rounded-2xl ${m.seq === Number.MAX_SAFE_INTEGER ? 'opacity-70' : ''}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : m.poll ? (
                <div key={m.id} className={`flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
                  {avatarCol}
                  <div className={`flex min-w-0 flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    {senderTag}
                    <PollMessage poll={m.poll} onVote={(optionIds) => onVotePoll?.(m.id, optionIds)} />
                  </div>
                </div>
              ) : m.file ? (
                <div key={m.id} className={`flex gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
                  {avatarCol}
                  <div className={`flex min-w-0 flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    {senderTag}
                    <FileMessage
                      msg={m}
                      canFileCompany={canFileCompany}
                      onFileToCategory={onFileToCategory ? (cat) => onFileToCategory(m.id, cat) : undefined}
                    />
                  </div>
                </div>
              ) : dana && m.proposals?.length ? (
                <div key={m.id} className="flex flex-col items-start gap-1">
                  <Bubble msg={m} mine={false} conversationTitle={title} onReact={(key) => onReact(m.id, key)} mentions={mentionTargets} />
                  {m.proposals.map((p) => (
                    <ProposalCard
                      key={p.action_id}
                      proposal={p}
                      ownerUserId={m.proposal_owner_user_id}
                      selfId={currentUserId}
                      nameOf={directory.nameOf}
                    />
                  ))}
                </div>
              ) : (
                <Bubble
                  key={m.id}
                  msg={m}
                  mine={mine}
                  mentions={mentionTargets}
                  senderName={senderName}
                  conversationTitle={title}
                  avatar={avatar}
                  receipts={receipts}
                  nameOf={directory.nameOf}
                  onReact={(key) => onReact(m.id, key)}
                  onEdit={onEdit ? (body) => onEdit(m.id, body) : undefined}
                  onDelete={onDelete ? () => onDelete(m.id) : undefined}
                  canDelete={!!onDelete && (mine || !!canModerate) && !dana && m.kind !== 'SYSTEM'}
                  onReply={onReply && m.kind === 'TEXT' ? () => onReply({ id: m.id, author: mine ? t('you', { defaultValue: 'You' }) : dana ? 'Dana' : directory.nameOf(m.sender_user_id), snippet: m.body_plain.slice(0, 140) }) : undefined}
                  quoted={(() => {
                    if (!m.reply_to_message_id) return null;
                    const parent = messages.find((x) => x.id === m.reply_to_message_id);
                    if (!parent) return null;
                    const author = parent.sender_user_id === currentUserId ? t('you', { defaultValue: 'You' }) : parent.sender_user_id === DANA_BOT_USER_ID ? 'Dana' : directory.nameOf(parent.sender_user_id);
                    return { author, snippet: parent.deleted_at ? t('delete.deleted', { defaultValue: 'Message deleted' }) : (parent.body_plain || parent.file?.name || 'GIF').slice(0, 140) };
                  })()}
                />
              );
            });
          })()
        )}
        <div ref={endRef} />
      </div>

      <div className="flex-none border-t border-border p-3">
        {typingLabel ? (
          <div className="px-4 pb-1 text-xs italic text-muted-foreground" aria-live="polite">
            {typingLabel}
          </div>
        ) : null}
        <Composer
          directory={directory}
          replyTo={replyTo}
          onCancelReply={onCancelReply}
          onSend={onSend}
          onSendGif={onSendGif}
          onSendPoll={onSendPoll}
          onSendFile={onSendFile}
          onCreateAssignment={onCreateAssignment}
          canAssign={canAssign}
          selfId={selfId}
          onTyping={onTyping}
          draftKey={draftKey}
          conversationId={conversationId}
          recentMessages={recognitionContext}
        />
      </div>
    </div>
  );
};
